import { createStoreProvider, makeReduxStore, redactForInspection, ReduxStore } from '@st/redux'
import { makePageTextRegistry, type PageTextResolver } from '@st/document-viewer-kit'

import { JsonMap } from '@st/util/json-value'
import { truncate } from '@st/util/truncate'
import { Cause, Effect, Layer } from 'effect'
import type { AppType } from './features/app-type'
import {
  ErrorReporting,
  BrowserResources,
  makeBrowserResourceRegistry,
  appRuntimeLayer,
  makeBrowserRouting,
  organizationContextLayer,
  Routing,
  requestSchedulerLayer,
  snackbarLayer,
  clipboardLayer,
  shortcutsLayer
} from './effects'
import { stProcessesForAppType, type STProcessMap } from './store-modules'
import { stDocViewerLiveLayer } from './st-doc-viewer/runtime-definition'
import { installDevStore } from './util/dev-store-registry'
import { installQaHelpers } from './util/redux-devtools'

type StoreDeps = {
  appType: AppType
  browserOrigin: string
  devMode: boolean
  enableDevtools: boolean
}

export type STAppStore = {
  store: ReduxStore<STProcessMap>
  browserResources: ReturnType<typeof makeBrowserResourceRegistry>
  resolvePageText: PageTextResolver
}

export function makeSTAppStore(deps: StoreDeps) {
  return Effect.gen(function* () {
    let store: ReduxStore<STProcessMap>
    const browserResources = makeBrowserResourceRegistry()
    const pageTextRegistry = makePageTextRegistry()
    const runtimeLayer = appRuntimeLayer(deps)
    const routing = yield* makeBrowserRouting
    const initialRoute = yield* routing.currentLocation

    yield* Effect.addFinalizer(() =>
      Effect.sync(() => {
        browserResources.clear()
        pageTextRegistry.clear()
      })
    )

    const baseLayer = Layer.mergeAll(
      organizationContextLayer().pipe(
        Layer.provide(Layer.mergeAll(runtimeLayer, Layer.succeed(Routing, routing)))
      ),
      clipboardLayer(),
      shortcutsLayer(),
      snackbarLayer(),
      requestSchedulerLayer(),
      stDocViewerLiveLayer.pipe(Layer.provide(pageTextRegistry.layer)),
      Layer.succeed(Routing, routing),
      Layer.succeed(BrowserResources, browserResources.service)
    ).pipe(Layer.provideMerge(runtimeLayer))

    const runBackgroundEffect = <A, E>(
      label: string,
      effect: Effect.Effect<A, E, ErrorReporting>
    ) => {
      if (!store) {
        return
      }

      store.forkBackgroundEffect(
        label,
        effect.pipe(
          Effect.onError((cause) => Effect.logError(`[st-store] ${label}\n${Cause.pretty(cause)}`)),
          Effect.ignore
        )
      )
    }

    const processes = stProcessesForAppType({ appType: deps.appType })
    store = yield* makeReduxStore<STProcessMap>({
      name: 'st',
      processes,
      initArgs: {
        route: initialRoute,
      },
      effectLayer: baseLayer,
      enableTimeline: Boolean(import.meta.env.DEV),
      enableDevtools: deps.enableDevtools,
      onEvent: (event) => {
        const inspectionEvent = sanitizeStoreAction(event)
        runBackgroundEffect(
          'redux breadcrumb capture failed',
          Effect.gen(function* () {
            const errorReporting = yield* ErrorReporting
            yield* errorReporting.addBreadcrumb({
              category: 'redux',
              message: event.type,
              data: truncate(inspectionEvent as JsonMap, {
                maxArrayLength: 10,
                maxKeys: 10,
                maxStringLength: 100
              }),
              level: 'info'
            })
          })
        )
      },
      onError: (error) => {
        runBackgroundEffect(
          'redux error capture failed',
          Effect.gen(function* () {
            const errorReporting = yield* ErrorReporting
            yield* errorReporting.captureException(error)
          })
        )
      }
    })

    yield* installDevStore('st', store)
    yield* installQaHelpers('st')

    return { store, browserResources, resolvePageText: pageTextRegistry.resolve }
  })
}

function sanitizeStoreAction(action: { type: string }): { type: string; [key: string]: unknown } {
  return redactForInspection(action) as {
    type: string
    [key: string]: unknown
  }
}

const { StoreProvider, useSelect, useProcess } = createStoreProvider<STProcessMap>()

export { StoreProvider, useSelect, useProcess }
